import type { ReactElement } from "react";
import { ContextMenuAction } from "../../../../types/contextMenuAction";
import { SummaryContext } from "../../shared/ActionsMenuContext";
import { ValidateMergeRequestResult } from "../../../../hooks/useValidateMergeRequest";
import { PlanTableFormat } from "../../../../types/planning/plan.type";
import ContextMenuSelectionCount from "../../../../components/shared/ContextMenuSelectionCount";
import { unifyShipmentContext } from "../../../../utils/unifyShipmentContext";
import {
  isRouteImmutablePure,
  isRouteClosedForSequencing,
} from "../../../../hooks/useIsRouteImmutable";
import { checkIsTrapCancelled } from "../../../../hooks/useIsTrapCancelled";

import { AutoSequenceMenuItem } from "./AutoSequenceMenuItem";
import MergeMenuItem from "./MergeMenuItem";
import { DeletePlanMenuItem } from "./DeletePlanMenuItem";
import { EditMenuItem } from "./EditMenuItem";
import { PrintMenuItem } from "./PrintMenuItem";
import { CopyLinkMenuItem } from "./CopyLinkMenuItem";
import { UnlockRoutesMenuItem } from "./UnlockRoutesMenuItem";
import { LockRoutesMenuItem } from "./LockRoutesMenuItem";
import { CreateRouteTrapMenuItem } from "./CreateRouteTrapMenuItem";
import { AssignToMenuItem } from "./AssignToMenuItem";
import { UnassignMenuItem } from "./UnassignMenuItem";
import { ChangeStatusMenuItem } from "./ChangeStatusMenuItem";

type PlanningContextMenuActionsArgs =
  | {
      plannedRoutesOrTraps: PlanTableFormat[];
      routeOrTrap?: never;
      shipmentContext: SummaryContext[];
      section: "planning";
      clearSelection?: () => void;
      validateMergeRequest: () => ValidateMergeRequestResult;
      isClosedForSequencingFlag?: boolean;
      isCNCLTrapFeatureEnabled: boolean;
    }
  | {
      plannedRoutesOrTraps?: never;
      routeOrTrap: PlanTableFormat | null;
      shipmentContext: SummaryContext[];
      section: "shipments";
      clearSelection?: () => void;
      validateMergeRequest?: never;
      isClosedForSequencingFlag?: boolean;
      isCNCLTrapFeatureEnabled: boolean;
    };

export const planningContextMenuActions = ({
  plannedRoutesOrTraps = [],
  routeOrTrap,
  shipmentContext,
  section,
  clearSelection,
  validateMergeRequest,
  isClosedForSequencingFlag = false,
  isCNCLTrapFeatureEnabled,
}: PlanningContextMenuActionsArgs): ContextMenuAction[] => {
  const unifiedContext = unifyShipmentContext(shipmentContext);

  const routesAndTrapsArray: PlanTableFormat[] =
    section === "shipments"
      ? routeOrTrap
        ? [routeOrTrap]
        : []
      : plannedRoutesOrTraps;

  const selectedRoutesAndTraps = routesAndTrapsArray.filter((plan) =>
    unifiedContext.some((ctx) => String(ctx.id) === String(plan.id))
  );

  // Block context menu if any selected item is a CNCL trap
  const hasSelectedCNCLTrap = selectedRoutesAndTraps.some(
    (plan) =>
      plan.type === "delivery_trap" &&
      checkIsTrapCancelled(plan.status, isCNCLTrapFeatureEnabled)
  );

  if (hasSelectedCNCLTrap) {
    return [];
  }

  // reuse hook logic via pure helper
  const isImmutableRoute = (route: PlanTableFormat) =>
    isRouteImmutablePure(route.status, route.shipments);

  const isImmutableSingleRoute =
    selectedRoutesAndTraps.length === 1 &&
    selectedRoutesAndTraps[0].type === "delivery_route" &&
    isImmutableRoute(selectedRoutesAndTraps[0]);

  const isImmutableAnyRoute = selectedRoutesAndTraps.some(
    (r) => r.type === "delivery_route" && isImmutableRoute(r)
  );

  const isClosedForSequencing = isClosedForSequencingFlag
    ? isRouteClosedForSequencing(selectedRoutesAndTraps)
    : isImmutableAnyRoute;

  const lockedRoutes = selectedRoutesAndTraps.filter(
    (routeOrTrap) =>
      routeOrTrap.isCommitted && routeOrTrap.type === "delivery_route"
  );
  const unlockedRoutes = selectedRoutesAndTraps.filter(
    (routeOrTrap) =>
      Boolean(!routeOrTrap.isCommitted) && routeOrTrap.type === "delivery_route"
  );

  const actionItem = (
    label: string,
    itemComponent: ReactElement<
      { handleClose?: () => void } & Record<string, unknown>
    >,
    keepOpenAfterClick = false
  ): ContextMenuAction => ({
    label,
    itemComponent,
    keepOpenAfterClick,
  });

  const actions: ContextMenuAction[] = [];

  const selectionCount =
    section === "planning"
      ? shipmentContext.length
      : shipmentContext.flatMap((shipment) => shipment.shipmentIds).length;

  if (selectionCount > 1) {
    actions.push(
      actionItem(
        "Selection count",
        <ContextMenuSelectionCount count={selectionCount} />,
        true
      )
    );
  }
  if (section === "planning") {
    if (
      selectedRoutesAndTraps.some((r) => r.type === "delivery_route") &&
      !isClosedForSequencing
    ) {
      actions.push(
        actionItem(
          "Auto-Sequence",
          <AutoSequenceMenuItem routesOrTraps={selectedRoutesAndTraps} />
        )
      );
    }
    // Print always available
    actions.push(
      actionItem(
        "Print",
        <PrintMenuItem
          routesOrTraps={selectedRoutesAndTraps}
          clearSelection={clearSelection}
          isBulkPrint={selectedRoutesAndTraps.length > 1}
        />
      )
    );
    // Lock/Unlock for non-immutable selections
    if (!isImmutableAnyRoute) {
      if (lockedRoutes.length > 0) {
        actions.push(
          actionItem(
            "Unlock route",
            <UnlockRoutesMenuItem
              routes={lockedRoutes}
              clearSelection={clearSelection}
            />
          )
        );
      }
      if (unlockedRoutes.length > 0) {
        actions.push(
          actionItem(
            "Lock route",
            <LockRoutesMenuItem
              routes={unlockedRoutes}
              clearSelection={clearSelection}
            />
          )
        );
      }
    }
    // Merge: hide when any selected route is immutable
    if (selectedRoutesAndTraps.length > 1 && !isImmutableAnyRoute) {
      actions.push(
        actionItem(
          "Merge",
          <MergeMenuItem
            planSummaries={selectedRoutesAndTraps}
            shipmentContext={shipmentContext}
            validateMergeRequest={validateMergeRequest}
            clearSelection={clearSelection}
          />,
          true
        )
      );
    }
    // Change Status: hide if any selected route is immutable
    if (!isImmutableAnyRoute) {
      actions.push(
        actionItem(
          "Change Status",
          <ChangeStatusMenuItem
            routesOrTraps={selectedRoutesAndTraps}
            isParentOpen={true}
            clearSelection={clearSelection}
          />
        )
      );
    }
    // Copy link always available
    actions.push(
      actionItem(
        "Copy link",
        <CopyLinkMenuItem routesOrTraps={selectedRoutesAndTraps} />
      )
    );
    // Edit hidden for immutable single route
    if (selectedRoutesAndTraps.length === 1 && !isImmutableSingleRoute) {
      actions.push(
        actionItem(
          "Edit",
          <EditMenuItem
            routeOrTrap={selectedRoutesAndTraps[0]}
            clearSelection={clearSelection}
          />
        )
      );
    }
    // Delete: hide when any selected route is immutable
    if (!isImmutableAnyRoute) {
      actions.push(
        actionItem(
          "Delete",
          <DeletePlanMenuItem
            routesOrTraps={selectedRoutesAndTraps}
            clearSelection={clearSelection}
          />
        )
      );
    }
  } else if (routeOrTrap) {
    // hide shipment actions for immutable routes
    if (!isImmutableRoute(routeOrTrap)) {
      actions.push(
        actionItem(
          "Create Route or Trap",
          <CreateRouteTrapMenuItem
            isParentOpen={true}
            routesOrTraps={[routeOrTrap]}
            shipmentContext={shipmentContext}
            clearSelection={clearSelection}
          />
        ),
        actionItem(
          "Assign To",
          <AssignToMenuItem
            routeOrTrap={routeOrTrap}
            shipmentContext={shipmentContext}
          />
        ),
        actionItem(
          "Unassign",
          <UnassignMenuItem
            routesOrTraps={[routeOrTrap]}
            shipmentContext={shipmentContext}
            clearSelection={clearSelection}
          />
        )
      );
    }
  }

  return actions;
};
